/**
 * This module contains all the code that is ran as the JS is parsed and on all pages
 *
 * @module global
 */

import loadPageModules from './modules/load-page-modules/load-page-modules';
import lazySizes from 'lazysizes';

import 'lazysizes/plugins/respimg/ls.respimg';
import 'lazysizes/plugins/optimumx/ls.optimumx';
import 'lazysizes/plugins/native-loading/ls.native-loading';
import customSelect from './modules/custom-select/custom-select';
import * as slideoutMenu from './modules/slideout-menu/slideout-menu';
import { detectInternetExplorer } from './modules/ie-detect/ie-detect';
import { registerServiceWorker } from '@remarkablenet/rcm-sw';
import {
  openOffCanvasPanel,
  closeOffCanvasPanel,
  addOffCanvasPanelListeners,
} from './modules/off-canvas-panel/off-canvas-panel';

import VybeCmsIntegration from './modules/vybe-integration/vybe-integration';

lazySizes.cfg.hFac = 0.1;
lazySizes.cfg.loadMore = 2;
lazySizes.cfg.loadHidden = false;

import '../css/global.scss';
import '@remarkablenet/panel-updater/dist/index.css';
import {
  debounce,
  disableScroll,
  enableScroll
} from './modules/utility-functions/utility-functions';
import { deactivate } from './modules/css-transition-helper/css-transition-helper';
import { closeDSearch } from './modules/search/search-ui';
import bloomreachInit from "./modules/bloomreach/bloomreach";
/**
 * This module contains all the code that is ran as the JS is parsed for all pages
 *
 * Add code to this section that should be ran on all pages and is mission critical
 * Do not add things like carousel instanciation into this file
 * @name global
 * @async
 */

let menuInitiated = false;
let searchInitiated = false;
let searchModuleLoaded = false;

const windowWidth = window.innerWidth;

export default async () => {
  await detectInternetExplorer();


  if (/\bPWAShell\b/i.test(navigator.userAgent || '')) {
    console.log('PWAShell detected');
    document.documentElement.classList.add('force-pwa');
    import(/* webpackChunkName: "pwa-ios" */ '../css/layout/pwa/_ios.scss');
  }

  // Init Page Modules
  setTimeout(() => {
    console.log('Running Global Setup');
    // register the service worker
    registerServiceWorker({ swPath: '/service-worker.js' });
    window.addEventListener(
      'resize',
      debounce(() => {
        if (window.innerWidth === windowWidth) return;
        resetMenu();
        checkToInitiateMobileMenu();
        checkToInitiateSearch();
      }, 500)
    );

    window.innerWidth < 992 ? initiateMobileMenu() : null;
    initiateSearch();
    loadPageModules();
    customSelect();
    VybeCmsIntegration();
  }, 0);
  addOffCanvasPanelListeners();

  //Temporarily remove pwa install popup
  if (!document.body.classList.contains('checkoutThankyou')) window.addEventListener('beforeinstallprompt', (event) => { event.preventDefault() });

  bloomreachInit();
  onetrustSEO();
};

const onetrustSEO = () => {
  setTimeout(() => {
    const link = document.querySelector<HTMLLinkElement>('.privacy-notice-link');
    if (link) {
      link.innerHTML = link.getAttribute('aria-label');
    }
  }, 1000);
};

// --------------------SEARCH--------------------//
function checkToInitiateSearch() {
  if (searchInitiated) return;
  initiateSearch();
}

function loadSearch() {
  if (!searchModuleLoaded) {
    import('./modules/search/search-ui').then((Search) => {
      searchModuleLoaded = true;
      const searchContainerElm = document.querySelector(Search.DEFAULTS.searchContainer) as HTMLElement;
      Search.init();
      Search.openSearchPanel(searchContainerElm).then(() => {
        (searchContainerElm.querySelector(Search.DEFAULTS.searchInput) as HTMLInputElement).focus();
      });
      Search.getDefaultState();
      setTimeout(() => { closeDSearch(); }, 1000);
    });
  }
}

function initiateSearch() {
  const searchToggles = document.querySelectorAll('.js-toggle-search');
  const searchInput = document.querySelector('.search-container .js-search-input');
  const urlSearchParams = new URLSearchParams(window.location.search);
  if (searchInput) {
    searchInput.addEventListener(
      'keyup',
      (e) => {
        e.preventDefault();
        loadSearch();
        searchInitiated = true;
      },
      {
        once: true
      }
    );
    searchInput.addEventListener(
      'click',
      (e) => {
        e.preventDefault();
        loadSearch();
        searchInitiated = true;
      },
      {
        once: true
      }
    );
    searchInput.addEventListener(
      'paste',
      (e) => {
        loadSearch();
        searchInitiated = true;
      },
      {
        once: true
      }
    );
  }
  if (searchToggles.length > 0 && windowWidth < 992) {
    searchToggles.forEach((searchToggle) => {
      searchToggle.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          loadSearch();
          searchInitiated = true;
        },
        {
          once: true
        }
      );
    });
  }

  if (checkToTriggerSearchFromPWA()) {
    loadSearch();
    searchInitiated = true;
  }
}

const checkToTriggerSearchFromPWA = () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const mediumIsPwa = urlSearchParams.has('utm_medium') && urlSearchParams.get('utm_medium').toLowerCase() === 'pwa';
  const shortcutIsSearch = urlSearchParams.has('shortcut') && urlSearchParams.get('shortcut').toLowerCase() === 'search';

  return mediumIsPwa && shortcutIsSearch;
}


//--------------------MENU--------------------//

const resetMenu = () => document.dispatchEvent(new Event('rc-close-menu-fn'));

function checkToInitiateMobileMenu() {
  if (menuInitiated) return;
  if (window.innerWidth < 992) initiateMobileMenu();
}

function initiateMobileMenu() {
  const { menuOpen, menuClose } = slideoutMenu.default();
  menuInitiated = true;
  const drawer = document.querySelector('.nav-drawer');
  // setup mobile slide out menu

  document.querySelectorAll('.js-open-menu-panel').forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const link = e.currentTarget as HTMLElement;
      Array.from(document.querySelectorAll('.js-open-menu-panel'))
        .filter((otherLink) => otherLink !== link)
        .forEach((otherLink) => otherLink.parentElement.classList.remove('open'));

      //const canvasPanel = link.parentNode.querySelector('.off-canvas-panel');
      const canvasPanel = link.nextElementSibling;
      const canvalPanelActive = canvasPanel.classList.contains('is-active') && canvasPanel.classList.contains('is-visible');
      const allPanels = document.querySelectorAll<HTMLElement>('.nav-main-links--has-dropdown .dropdown-menu');
      const dropdownContainer = link.parentElement;

      const url = link.getAttribute('href');
      const hasSecondaryLinks = link.nextElementSibling.querySelector('.nav-secondary-links') !== null ? true : false;

      if (!hasSecondaryLinks) {
        window.location.href = url;
        return;
      }

      allPanels.forEach((panel) => { panel.classList.remove('is-visible', 'is-active'); });

      if (canvasPanel && !canvalPanelActive) {
        openOffCanvasPanel(canvasPanel);
        dropdownContainer?.classList.add('open');
        dropdownContainer?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (canvasPanel && canvalPanelActive) dropdownContainer?.classList.remove('open');

    });
  });

  document.querySelectorAll('.nav-secondary-links__link--has-dropdown > a').forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();

      link.classList.toggle('open');
      link.nextElementSibling.classList.toggle('open');
    });
  });

  document.querySelectorAll('.js-quick-open-menu-panel').forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const linkTarget = link.getAttribute('data-target');
      const targettedMenupanel = document.querySelector(`.js-open-menu-panel[data-nav-title="${linkTarget}"]`);

      Array.from(document.querySelectorAll('.js-open-menu-panel'))
        .filter((otherLink) => otherLink !== targettedMenupanel)
        .forEach((otherLink) => otherLink.parentElement.classList.remove('open'));

      const canvasPanel = targettedMenupanel.nextElementSibling;
      if (canvasPanel) {
        openOffCanvasPanel(canvasPanel);
        const dropdownContainer = link.parentElement;
        dropdownContainer.classList.toggle('open');
      }
      disableScroll();
      menuOpen();
    });
  });

  document.addEventListener('rc-close-menu-fn', () => {
    document.body.classList.remove('slideout-panel');
    document.documentElement.classList.remove('slideout-open');
    menuClose();
    enableScroll();
  });

  document.addEventListener('rc-menu-closed', () => {
    document.querySelectorAll('.nav-main-links--has-dropdown').forEach((dropdown) => {
      dropdown.classList.remove('open');
      deactivate(dropdown.querySelector('.dropdown-menu'));
    });
  });
}